import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { ColorGroup, ColorToken } from "../types";
import {
  createMergedGroups,
  parseObjectColorsToGroups,
} from "../utils/colorUtils";
import { fetchDesignTokens } from "../utils/designTokensApi";
import { generateAllTokenClasses } from "../utils/tailwindClassGeneration";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import type { SessionResource, SignedInSessionResource } from "@clerk/types";

export const UNGROUPED = "Ungrouped";

// Types for global state management
export interface DesignTokenApiResponse {
  colors: Record<string, string | Record<string, string>>; // Nested or flat structure
  colorsDark?: Record<string, string | Record<string, string>>; // Optional dark colors
}
interface DesignTokensStore {
  // Current view colors (changes based on light/dark mode)
  colors: ColorToken[];
  colorGroups: ColorGroup[];

  // Raw data from API
  lightColors: ColorToken[];
  lightColorGroups: ColorGroup[];
  darkColors: ColorToken[];
  darkColorGroups: ColorGroup[];

  // Merged tokens with fallback indicators
  mergedColorGroups: ColorGroup[];
  mergedColors: ColorToken[];

  // Status
  loading: boolean;
  firstLoadDone: boolean;
  hasDarkModeColors: boolean;
  error: string | null;

  // Last api response
  lastApiResponse: DesignTokenApiResponse | null;

  initializedCanvasId: string | null;
  lastRawApiResponse: DesignTokenApiResponse | null;
  // Core UI State
  activeTab: "colors" | "text-styles";
  searchQuery: string;
  expandedGroups: Set<string>;

  setColors: (colors: ColorToken[]) => void;
  setColorGroups: (groups: ColorGroup[]) => void;
  setLightColors: (colors: ColorToken[]) => void;
  setLightColorGroups: (groups: ColorGroup[]) => void;
  setDarkColors: (colors: ColorToken[]) => void;
  setDarkColorGroups: (groups: ColorGroup[]) => void;
  setLoading: (loading: boolean) => void;
  setFirstLoadDone: (done: boolean) => void;
  setError: (error: string | null) => void;
  updateFromApiResponse: (
    lightColorGroups: ColorGroup[],
    lightColors: ColorToken[],
    darkColorGroups: ColorGroup[],
    darkColors: ColorToken[],
    isViewDarkMode: boolean,
  ) => void;
  updateMergedTokens: (isViewDarkMode: boolean) => void;

  setInitializedCanvasId: (canvasId: string | null) => void;
  setLastRawApiResponse: (response: DesignTokenApiResponse | null) => void;

  // API Operations
  fetchColors: (
    canvasId: string,
    session: SessionResource,
    cssCustomProperties?: {
      root?: Record<string, string>;
      dark?: Record<string, string>;
    } | null,
    showLoading?: boolean,
  ) => Promise<void>;
  syncFromCssCustomProperties: (
    cssCustomProperties: {
      root?: Record<string, string>;
      dark?: Record<string, string>;
    } | null,
  ) => void;
  syncFromApiResponse: (
    response: DesignTokenApiResponse,
    isViewDarkMode: boolean,
    cssCustomProperties?: {
      root?: Record<string, string>;
      dark?: Record<string, string>;
    } | null,
  ) => void;
  updateTailwindClasses: () => void;
  processApiResponseWithCss: (
    response: DesignTokenApiResponse,
    isViewDarkMode: boolean,
    cssCustomProperties?: {
      root?: Record<string, string>;
      dark?: Record<string, string>;
    } | null,
  ) => void;

  setActiveTab: (tab: "colors" | "text-styles") => void;
  setSearchQuery: (query: string) => void;
  setExpandedGroups: (groups: Set<string>) => void;
  toggleGroup: (groupName: string) => void;
  expandGroup: (groupName: string) => void;

  initializeExpandedGroups: (groupNames: string[]) => void;
  updateExpandedGroupsOnDataChange: (
    currentGroupNames: string[],
    newGroups: Array<{ name: string; isExpanded?: boolean }>,
  ) => void;
}

export const useDesignTokensStore = create<DesignTokensStore>()(
  persist(
    (set) => ({
      initializedCanvasId: null,
      lastRawApiResponse: null,

      colors: [],
      colorGroups: [],
      lightColors: [],
      lightColorGroups: [],
      darkColors: [],
      darkColorGroups: [],
      mergedColorGroups: [],
      mergedColors: [],
      loading: false,
      firstLoadDone: false,
      hasDarkModeColors: false,
      error: null,
      activeTab: "colors" as "colors" | "text-styles",
      searchQuery: "",
      expandedGroups: new Set<string>(),
      lastApiResponse: null,

      setColors: (colors) => set({ colors }),
      setColorGroups: (colorGroups) => set({ colorGroups }),
      setLightColors: (lightColors) => set({ lightColors }),
      setLightColorGroups: (lightColorGroups) => set({ lightColorGroups }),
      setDarkColors: (darkColors) => set({ darkColors }),
      setDarkColorGroups: (darkColorGroups) => set({ darkColorGroups }),
      setLoading: (loading) => set({ loading }),
      setFirstLoadDone: (firstLoadDone) => set({ firstLoadDone }),
      setError: (error) => set({ error }),
      updateFromApiResponse: (
        lightColorGroups,
        lightColors,
        darkColorGroups,
        darkColors,
        isViewDarkMode,
      ) => {
        // Calculate hasDarkModeColors
        const hasDarkModeColors =
          (darkColorGroups?.length || 0) > 0 || (darkColors?.length || 0) > 0;

        // Create merged tokens
        const mergedTokens = createMergedGroups(
          lightColorGroups,
          lightColors,
          darkColorGroups,
          darkColors,
          isViewDarkMode,
        );

        set({
          // Always store all data, but let the useDesignTokens hook handle view filtering
          lightColors,
          lightColorGroups,
          darkColors,
          darkColorGroups,
          colors: lightColors, // Default to light colors
          colorGroups: lightColorGroups, // Default to light groups
          mergedColorGroups: mergedTokens.groups,
          mergedColors: mergedTokens.colors,
          hasDarkModeColors,
          loading: false,
          firstLoadDone: true,
        });
      },

      updateMergedTokens: (isViewDarkMode) => {
        const state = useDesignTokensStore.getState();
        const mergedTokens = createMergedGroups(
          state.lightColorGroups,
          state.lightColors,
          state.darkColorGroups,
          state.darkColors,
          isViewDarkMode,
        );
        set({
          mergedColorGroups: mergedTokens.groups,
          mergedColors: mergedTokens.colors,
        });
      },

      setInitializedCanvasId: (initializedCanvasId) =>
        set({ initializedCanvasId }),

      setLastRawApiResponse: (lastRawApiResponse) =>
        set({ lastRawApiResponse }),

      setActiveTab: (tab) => set({ activeTab: tab }),

      setSearchQuery: (query) => set({ searchQuery: query }),

      setExpandedGroups: (groups) => set({ expandedGroups: groups }),

      toggleGroup: (groupName) =>
        set((state) => {
          const newExpandedGroups = new Set(state.expandedGroups);
          if (newExpandedGroups.has(groupName)) {
            newExpandedGroups.delete(groupName);
          } else {
            newExpandedGroups.add(groupName);
          }
          return { expandedGroups: newExpandedGroups };
        }),

      expandGroup: (groupName) =>
        set((state) => ({
          expandedGroups: new Set([...state.expandedGroups, groupName]),
        })),

      initializeExpandedGroups: (groupNames) =>
        set({
          expandedGroups: new Set(groupNames),
        }),

      updateExpandedGroupsOnDataChange: (currentGroupNames, newGroups) =>
        set((state) => {
          const existingGroupNames = new Set(currentGroupNames);
          const newExpandedGroups = new Set<string>();

          // Keep only groups that still exist
          for (const name of state.expandedGroups) {
            if (existingGroupNames.has(name)) {
              newExpandedGroups.add(name);
            }
          }

          // Add newly added groups if backend marks them expanded
          for (const group of newGroups) {
            if (!state.expandedGroups.has(group.name) && group.isExpanded) {
              newExpandedGroups.add(group.name);
            }
          }

          return { expandedGroups: newExpandedGroups };
        }),

      fetchColors: async (
        canvasId,
        session,
        cssCustomProperties,
        showLoading = true,
      ) => {
        const state = useDesignTokensStore.getState();

        if (
          showLoading &&
          state.lightColors.length === 0 &&
          state.lightColorGroups.length === 0
        ) {
          set({ loading: true });
        }

        set({ error: null });

        try {
          const apiResponse = await fetchDesignTokens(
            canvasId,
            session as SignedInSessionResource,
          );
          set({ lastApiResponse: apiResponse });
          state.syncFromApiResponse(apiResponse, false, cssCustomProperties);
        } catch (err: unknown) {
          const errorMessage =
            err instanceof Error ? err.message : "Failed to load design tokens";
          set({ error: errorMessage });
        } finally {
          set({ loading: false });
        }
      },

      syncFromCssCustomProperties: (cssCustomProperties) => {
        const state = useDesignTokensStore.getState();
        if (!state.lastApiResponse) {
          return;
        }

        try {
          const apiResponse = state.lastApiResponse;
          if (!apiResponse) {
            return;
          }

          state.syncFromApiResponse(apiResponse, false, cssCustomProperties);
        } catch (err: unknown) {
          console.error(err);
          set({ error: "Failed to load design tokens" });
        }
      },

      syncFromApiResponse: (
        apiResponse,
        isViewDarkMode,
        cssCustomProperties,
      ) => {
        const state = useDesignTokensStore.getState();
        set({ lastRawApiResponse: apiResponse });

        const darkColorsObj = apiResponse.colorsDark;
        const {
          groups: serverGroups,
          ungroupedColors: serverUngrouped,
          darkGroups: serverDarkGroups,
          darkUngroupedColors: serverDarkUngrouped,
        } = parseObjectColorsToGroups(
          apiResponse.colors,
          darkColorsObj,
          cssCustomProperties?.root,
          cssCustomProperties?.dark,
        );

        state.updateFromApiResponse(
          serverGroups,
          serverUngrouped,
          serverDarkGroups,
          serverDarkUngrouped,
          isViewDarkMode,
        );
      },

      processApiResponseWithCss: (
        response,
        isViewDarkMode,
        cssCustomProperties,
      ) => {
        const state = useDesignTokensStore.getState();
        if (!cssCustomProperties) return;

        const darkColorsObj = response.colorsDark;
        const {
          groups: serverGroups,
          ungroupedColors: serverUngrouped,
          darkGroups: serverDarkGroups,
          darkUngroupedColors: serverDarkUngrouped,
        } = parseObjectColorsToGroups(
          response.colors,
          darkColorsObj,
          cssCustomProperties?.root,
          cssCustomProperties?.dark,
        );

        state.updateFromApiResponse(
          serverGroups,
          serverUngrouped,
          serverDarkGroups,
          serverDarkUngrouped,
          isViewDarkMode,
        );
      },

      updateTailwindClasses: () => {
        const state = useDesignTokensStore.getState();
        const tailwindClasses = useCodebaseStore.getState().tailwindClasses;

        if (tailwindClasses.length === 0) {
          return;
        }

        const newTokenClasses = generateAllTokenClasses(
          state.lightColorGroups,
          state.lightColors,
          state.darkColorGroups,
          state.darkColors,
        );

        const designTokenClassNames = new Set<string>();
        for (const cls of newTokenClasses) {
          designTokenClassNames.add(cls.tailwind);
        }

        const filteredExistingClasses = tailwindClasses.filter((cls) => {
          return !designTokenClassNames.has(cls.tailwind);
        });

        const existingFilteredNames = new Set(
          filteredExistingClasses.map((cls) => cls.tailwind),
        );

        const uniqueNewClasses = newTokenClasses.filter(
          (cls) => !existingFilteredNames.has(cls.tailwind),
        );

        const updatedClasses = [
          ...filteredExistingClasses,
          ...uniqueNewClasses,
        ];
        useCodebaseStore.getState().setTailwindClasses(updatedClasses);
      },
    }),
    {
      name: "design-tokens-store",
      // Persist both data and UI state, but be selective
      partialize: (state) => ({
        // UI state - always persist
        activeTab: state.activeTab,
        searchQuery: state.searchQuery,
        expandedGroups: Array.from(state.expandedGroups),

        // Data state - persist to avoid reloading
        colors: state.colors,
        colorGroups: state.colorGroups,
        lightColors: state.lightColors,
        lightColorGroups: state.lightColorGroups,
        darkColors: state.darkColors,
        darkColorGroups: state.darkColorGroups,
        mergedColorGroups: state.mergedColorGroups,
        mergedColors: state.mergedColors,
        hasDarkModeColors: state.hasDarkModeColors,
        firstLoadDone: state.firstLoadDone,

        initializedCanvasId: state.initializedCanvasId,
        lastRawApiResponse: state.lastRawApiResponse,
      }),

      onRehydrateStorage: () => (state) => {
        if (state && Array.isArray(state.expandedGroups)) {
          state.expandedGroups = new Set(state.expandedGroups);
        }
        if (state) {
          state.loading = false;
        }
      },
    },
  ),
);
